// sQMS Mock Data - Schedule-M Compliance Platform
// Indian Pharmaceutical Manufacturing Data with Plant-specific filtering

// Helper functions for Indian formatting
export const formatIndianCurrency = (amount) => {
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(2)} Cr`;
  } else if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(2)} Lakh`;
  }
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
};

export const formatIndianNumber = (num) => {
  return new Intl.NumberFormat('en-IN').format(num);
};

export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Plants and Areas
export const plants = [
  { id: 'PLANT-A', name: 'Ahmedabad (Plant A)', code: 'AHM' },
  { id: 'PLANT-B', name: 'Pune (Plant B)', code: 'PUN' }
];

export const areas = [
  { id: 'AREA-OSD', name: 'Block A – Oral Solid', plantId: 'PLANT-A', code: 'OSD' },
  { id: 'AREA-STER', name: 'Sterile Suite', plantId: 'PLANT-A', code: 'STER' },
  { id: 'AREA-WH', name: 'Warehouse', plantId: 'PLANT-A', code: 'WH' },
  { id: 'AREA-QC', name: 'QC Lab', plantId: 'PLANT-A', code: 'QC' },
  { id: 'AREA-OSD-B', name: 'Block B – Oral Solid', plantId: 'PLANT-B', code: 'OSD-B' },
  { id: 'AREA-STER-B', name: 'Sterile Suite B', plantId: 'PLANT-B', code: 'STER-B' },
  { id: 'AREA-WH-B', name: 'Warehouse B', plantId: 'PLANT-B', code: 'WH-B' },
  { id: 'AREA-QC-B', name: 'QC Lab B', plantId: 'PLANT-B', code: 'QC-B' }
];

// Helper to get areas by plant
export const getAreasByPlant = (plantId) => {
  return areas.filter(a => a.plantId === plantId);
};

// Roles for Role Switcher
export const roles = [
  { id: 'OPERATOR', name: 'Operator (Shopfloor)', icon: 'Wrench', permissions: ['execute_tasks', 'submit_issues'] },
  { id: 'PROD_SUP', name: 'Production Supervisor', icon: 'Users', permissions: ['execute_tasks', 'submit_issues', 'approve_tasks'] },
  { id: 'QA_OFFICER', name: 'QA Officer', icon: 'Shield', permissions: ['execute_tasks', 'approve_tasks', 'investigate_deviations'] },
  { id: 'QA_HEAD', name: 'QA Head', icon: 'ShieldCheck', permissions: ['all'] },
  { id: 'QC_ANALYST', name: 'QC Analyst', icon: 'FlaskConical', permissions: ['execute_tasks', 'lab_tests'] },
  { id: 'ENGINEERING', name: 'Engineering/Utilities', icon: 'Settings', permissions: ['execute_tasks', 'equipment_mgmt', 'calibration'] },
  { id: 'WAREHOUSE', name: 'Warehouse', icon: 'Package', permissions: ['execute_tasks', 'material_receipt'] },
  { id: 'INT_AUDITOR', name: 'Internal Auditor', icon: 'ClipboardCheck', permissions: ['execute_audits', 'view_all'] },
  { id: 'EXT_AUDITOR', name: 'External Auditor (Read-only)', icon: 'Eye', permissions: ['view_only'] },
  { id: 'CORPORATE', name: 'Corporate Quality (Management)', icon: 'Building2', permissions: ['view_all', 'reports'] }
];

// ============================================================
// PLANT-SPECIFIC DASHBOARD METRICS
// Different data for each plant/area/time combination
// ============================================================

export const dashboardMetricsByFilter = {
  // PLANT-A Data
  'PLANT-A': {
    'all': {
      '7days': {
        complianceScore: 91,
        overdueCriticalTasks: 4,
        openDeviations: 8,
        capaAging30Plus: 2,
        changeControlsPendingQA: 2,
        upcomingSelfInspections: 1,
        auditReadinessIndex: 86,
        trainingCompliance: 94,
        remediationBudget: 18500000,
        trainingSpendQ1: 850000,
        avgDeviationClosureCost: 95000,
        potentialAuditRiskExposure: 4200000
      },
      '30days': {
        complianceScore: 92,
        overdueCriticalTasks: 7,
        openDeviations: 14,
        capaAging30Plus: 5,
        changeControlsPendingQA: 3,
        upcomingSelfInspections: 1,
        auditReadinessIndex: 88,
        trainingCompliance: 96,
        remediationBudget: 32500000,
        trainingSpendQ1: 1850000,
        avgDeviationClosureCost: 120000,
        potentialAuditRiskExposure: 7500000
      },
      'quarter': {
        complianceScore: 90,
        overdueCriticalTasks: 12,
        openDeviations: 22,
        capaAging30Plus: 8,
        changeControlsPendingQA: 5,
        upcomingSelfInspections: 2,
        auditReadinessIndex: 85,
        trainingCompliance: 93,
        remediationBudget: 45000000,
        trainingSpendQ1: 3200000,
        avgDeviationClosureCost: 135000,
        potentialAuditRiskExposure: 12000000
      }
    },
    'AREA-OSD': {
      '7days': {
        complianceScore: 93,
        overdueCriticalTasks: 2,
        openDeviations: 4,
        capaAging30Plus: 1,
        changeControlsPendingQA: 1,
        upcomingSelfInspections: 0,
        auditReadinessIndex: 90,
        trainingCompliance: 97,
        remediationBudget: 8500000,
        trainingSpendQ1: 420000,
        avgDeviationClosureCost: 85000,
        potentialAuditRiskExposure: 2100000
      },
      '30days': {
        complianceScore: 94,
        overdueCriticalTasks: 3,
        openDeviations: 6,
        capaAging30Plus: 2,
        changeControlsPendingQA: 1,
        upcomingSelfInspections: 1,
        auditReadinessIndex: 91,
        trainingCompliance: 98,
        remediationBudget: 15000000,
        trainingSpendQ1: 920000,
        avgDeviationClosureCost: 110000,
        potentialAuditRiskExposure: 3500000
      },
      'quarter': {
        complianceScore: 92,
        overdueCriticalTasks: 5,
        openDeviations: 10,
        capaAging30Plus: 3,
        changeControlsPendingQA: 2,
        upcomingSelfInspections: 1,
        auditReadinessIndex: 88,
        trainingCompliance: 95,
        remediationBudget: 22000000,
        trainingSpendQ1: 1500000,
        avgDeviationClosureCost: 125000,
        potentialAuditRiskExposure: 5800000
      }
    },
    'AREA-STER': {
      '7days': {
        complianceScore: 89,
        overdueCriticalTasks: 1,
        openDeviations: 2,
        capaAging30Plus: 1,
        changeControlsPendingQA: 1,
        upcomingSelfInspections: 1,
        auditReadinessIndex: 85,
        trainingCompliance: 95,
        remediationBudget: 6200000,
        trainingSpendQ1: 280000,
        avgDeviationClosureCost: 150000,
        potentialAuditRiskExposure: 3800000
      },
      '30days': {
        complianceScore: 88,
        overdueCriticalTasks: 2,
        openDeviations: 4,
        capaAging30Plus: 2,
        changeControlsPendingQA: 1,
        upcomingSelfInspections: 1,
        auditReadinessIndex: 84,
        trainingCompliance: 94,
        remediationBudget: 11000000,
        trainingSpendQ1: 580000,
        avgDeviationClosureCost: 175000,
        potentialAuditRiskExposure: 6200000
      },
      'quarter': {
        complianceScore: 86,
        overdueCriticalTasks: 4,
        openDeviations: 7,
        capaAging30Plus: 3,
        changeControlsPendingQA: 2,
        upcomingSelfInspections: 1,
        auditReadinessIndex: 82,
        trainingCompliance: 92,
        remediationBudget: 16500000,
        trainingSpendQ1: 980000,
        avgDeviationClosureCost: 195000,
        potentialAuditRiskExposure: 9500000
      }
    },
    'AREA-WH': {
      '7days': {
        complianceScore: 90,
        overdueCriticalTasks: 1,
        openDeviations: 1,
        capaAging30Plus: 0,
        changeControlsPendingQA: 0,
        upcomingSelfInspections: 0,
        auditReadinessIndex: 88,
        trainingCompliance: 92,
        remediationBudget: 2500000,
        trainingSpendQ1: 120000,
        avgDeviationClosureCost: 65000,
        potentialAuditRiskExposure: 1200000
      },
      '30days': {
        complianceScore: 91,
        overdueCriticalTasks: 1,
        openDeviations: 2,
        capaAging30Plus: 1,
        changeControlsPendingQA: 1,
        upcomingSelfInspections: 0,
        auditReadinessIndex: 89,
        trainingCompliance: 94,
        remediationBudget: 4200000,
        trainingSpendQ1: 250000,
        avgDeviationClosureCost: 78000,
        potentialAuditRiskExposure: 1800000
      },
      'quarter': {
        complianceScore: 89,
        overdueCriticalTasks: 2,
        openDeviations: 3,
        capaAging30Plus: 1,
        changeControlsPendingQA: 1,
        upcomingSelfInspections: 0,
        auditReadinessIndex: 87,
        trainingCompliance: 91,
        remediationBudget: 6000000,
        trainingSpendQ1: 420000,
        avgDeviationClosureCost: 85000,
        potentialAuditRiskExposure: 2500000
      }
    },
    'AREA-QC': {
      '7days': {
        complianceScore: 94,
        overdueCriticalTasks: 0,
        openDeviations: 1,
        capaAging30Plus: 0,
        changeControlsPendingQA: 0,
        upcomingSelfInspections: 0,
        auditReadinessIndex: 92,
        trainingCompliance: 98,
        remediationBudget: 1800000,
        trainingSpendQ1: 95000,
        avgDeviationClosureCost: 55000,
        potentialAuditRiskExposure: 800000
      },
      '30days': {
        complianceScore: 95,
        overdueCriticalTasks: 1,
        openDeviations: 2,
        capaAging30Plus: 0,
        changeControlsPendingQA: 0,
        upcomingSelfInspections: 0,
        auditReadinessIndex: 93,
        trainingCompliance: 99,
        remediationBudget: 3200000,
        trainingSpendQ1: 180000,
        avgDeviationClosureCost: 62000,
        potentialAuditRiskExposure: 1200000
      },
      'quarter': {
        complianceScore: 93,
        overdueCriticalTasks: 1,
        openDeviations: 4,
        capaAging30Plus: 1,
        changeControlsPendingQA: 0,
        upcomingSelfInspections: 0,
        auditReadinessIndex: 91,
        trainingCompliance: 97,
        remediationBudget: 4800000,
        trainingSpendQ1: 320000,
        avgDeviationClosureCost: 72000,
        potentialAuditRiskExposure: 1800000
      }
    }
  },
  // PLANT-B Data
  'PLANT-B': {
    'all': {
      '7days': {
        complianceScore: 87,
        overdueCriticalTasks: 5,
        openDeviations: 9,
        capaAging30Plus: 3,
        changeControlsPendingQA: 2,
        upcomingSelfInspections: 1,
        auditReadinessIndex: 82,
        trainingCompliance: 91,
        remediationBudget: 21000000,
        trainingSpendQ1: 780000,
        avgDeviationClosureCost: 105000,
        potentialAuditRiskExposure: 5500000
      },
      '30days': {
        complianceScore: 85,
        overdueCriticalTasks: 9,
        openDeviations: 16,
        capaAging30Plus: 6,
        changeControlsPendingQA: 4,
        upcomingSelfInspections: 2,
        auditReadinessIndex: 80,
        trainingCompliance: 89,
        remediationBudget: 38000000,
        trainingSpendQ1: 1650000,
        avgDeviationClosureCost: 130000,
        potentialAuditRiskExposure: 9800000
      },
      'quarter': {
        complianceScore: 83,
        overdueCriticalTasks: 15,
        openDeviations: 28,
        capaAging30Plus: 10,
        changeControlsPendingQA: 7,
        upcomingSelfInspections: 3,
        auditReadinessIndex: 78,
        trainingCompliance: 86,
        remediationBudget: 52000000,
        trainingSpendQ1: 2800000,
        avgDeviationClosureCost: 155000,
        potentialAuditRiskExposure: 15000000
      }
    },
    'AREA-OSD-B': {
      '7days': {
        complianceScore: 88,
        overdueCriticalTasks: 2,
        openDeviations: 4,
        capaAging30Plus: 1,
        changeControlsPendingQA: 1,
        upcomingSelfInspections: 0,
        auditReadinessIndex: 84,
        trainingCompliance: 92,
        remediationBudget: 9500000,
        trainingSpendQ1: 380000,
        avgDeviationClosureCost: 95000,
        potentialAuditRiskExposure: 2800000
      },
      '30days': {
        complianceScore: 86,
        overdueCriticalTasks: 4,
        openDeviations: 7,
        capaAging30Plus: 3,
        changeControlsPendingQA: 2,
        upcomingSelfInspections: 1,
        auditReadinessIndex: 82,
        trainingCompliance: 90,
        remediationBudget: 17500000,
        trainingSpendQ1: 820000,
        avgDeviationClosureCost: 118000,
        potentialAuditRiskExposure: 4800000
      },
      'quarter': {
        complianceScore: 84,
        overdueCriticalTasks: 7,
        openDeviations: 12,
        capaAging30Plus: 5,
        changeControlsPendingQA: 3,
        upcomingSelfInspections: 1,
        auditReadinessIndex: 79,
        trainingCompliance: 87,
        remediationBudget: 25000000,
        trainingSpendQ1: 1350000,
        avgDeviationClosureCost: 140000,
        potentialAuditRiskExposure: 7500000
      }
    },
    'AREA-STER-B': {
      '7days': {
        complianceScore: 85,
        overdueCriticalTasks: 2,
        openDeviations: 3,
        capaAging30Plus: 1,
        changeControlsPendingQA: 1,
        upcomingSelfInspections: 1,
        auditReadinessIndex: 80,
        trainingCompliance: 90,
        remediationBudget: 7200000,
        trainingSpendQ1: 260000,
        avgDeviationClosureCost: 165000,
        potentialAuditRiskExposure: 4200000
      },
      '30days': {
        complianceScore: 83,
        overdueCriticalTasks: 3,
        openDeviations: 5,
        capaAging30Plus: 2,
        changeControlsPendingQA: 1,
        upcomingSelfInspections: 1,
        auditReadinessIndex: 78,
        trainingCompliance: 88,
        remediationBudget: 13000000,
        trainingSpendQ1: 520000,
        avgDeviationClosureCost: 190000,
        potentialAuditRiskExposure: 7200000
      },
      'quarter': {
        complianceScore: 80,
        overdueCriticalTasks: 5,
        openDeviations: 9,
        capaAging30Plus: 4,
        changeControlsPendingQA: 2,
        upcomingSelfInspections: 2,
        auditReadinessIndex: 75,
        trainingCompliance: 85,
        remediationBudget: 19500000,
        trainingSpendQ1: 880000,
        avgDeviationClosureCost: 215000,
        potentialAuditRiskExposure: 11000000
      }
    },
    'AREA-WH-B': {
      '7days': {
        complianceScore: 88,
        overdueCriticalTasks: 1,
        openDeviations: 1,
        capaAging30Plus: 1,
        changeControlsPendingQA: 0,
        upcomingSelfInspections: 0,
        auditReadinessIndex: 84,
        trainingCompliance: 90,
        remediationBudget: 2800000,
        trainingSpendQ1: 110000,
        avgDeviationClosureCost: 72000,
        potentialAuditRiskExposure: 1400000
      },
      '30days': {
        complianceScore: 86,
        overdueCriticalTasks: 1,
        openDeviations: 2,
        capaAging30Plus: 1,
        changeControlsPendingQA: 1,
        upcomingSelfInspections: 0,
        auditReadinessIndex: 82,
        trainingCompliance: 88,
        remediationBudget: 4800000,
        trainingSpendQ1: 220000,
        avgDeviationClosureCost: 85000,
        potentialAuditRiskExposure: 2200000
      },
      'quarter': {
        complianceScore: 84,
        overdueCriticalTasks: 2,
        openDeviations: 4,
        capaAging30Plus: 2,
        changeControlsPendingQA: 1,
        upcomingSelfInspections: 0,
        auditReadinessIndex: 80,
        trainingCompliance: 85,
        remediationBudget: 7200000,
        trainingSpendQ1: 380000,
        avgDeviationClosureCost: 98000,
        potentialAuditRiskExposure: 3200000
      }
    },
    'AREA-QC-B': {
      '7days': {
        complianceScore: 90,
        overdueCriticalTasks: 0,
        openDeviations: 1,
        capaAging30Plus: 0,
        changeControlsPendingQA: 0,
        upcomingSelfInspections: 0,
        auditReadinessIndex: 86,
        trainingCompliance: 94,
        remediationBudget: 2000000,
        trainingSpendQ1: 85000,
        avgDeviationClosureCost: 58000,
        potentialAuditRiskExposure: 950000
      },
      '30days': {
        complianceScore: 88,
        overdueCriticalTasks: 1,
        openDeviations: 2,
        capaAging30Plus: 0,
        changeControlsPendingQA: 0,
        upcomingSelfInspections: 0,
        auditReadinessIndex: 84,
        trainingCompliance: 92,
        remediationBudget: 3600000,
        trainingSpendQ1: 160000,
        avgDeviationClosureCost: 68000,
        potentialAuditRiskExposure: 1500000
      },
      'quarter': {
        complianceScore: 86,
        overdueCriticalTasks: 1,
        openDeviations: 3,
        capaAging30Plus: 1,
        changeControlsPendingQA: 1,
        upcomingSelfInspections: 0,
        auditReadinessIndex: 82,
        trainingCompliance: 90,
        remediationBudget: 5400000,
        trainingSpendQ1: 280000,
        avgDeviationClosureCost: 78000,
        potentialAuditRiskExposure: 2200000
      }
    }
  }
};

// Helper function to get metrics based on filters
export const getMetricsByFilter = (plantId, areaId, timeRange) => {
  const area = areaId === 'all' ? 'all' : areaId;
  const plantData = dashboardMetricsByFilter[plantId];
  if (!plantData) return dashboardMetricsByFilter['PLANT-A']['all']['30days'];
  
  const areaData = plantData[area];
  if (!areaData) return plantData['all'][timeRange] || plantData['all']['30days'];
  
  return areaData[timeRange] || areaData['30days'];
};

// ============================================================
// AT-RISK ITEMS BY FILTER
// ============================================================

export const atRiskItemsByFilter = {
  'PLANT-A': {
    'all': [
      { id: 1, title: 'HVAC differential pressure excursion – Sterile Suite', type: 'Utilities', severity: 'Critical', link: '/assets?id=AHU-STER-001', icon: 'ThermometerSun' },
      { id: 2, title: 'Calibration overdue – EQ-BAL-017', type: 'Equipment', severity: 'High', link: '/assets?id=EQ-BAL-017', icon: 'Scale' },
      { id: 3, title: 'Warehouse cold room temp excursion', type: 'Storage', severity: 'Critical', link: '/assets?id=CR-WH-001', icon: 'ThermometerSun' },
      { id: 4, title: 'Checklist failure: Line clearance incomplete', type: 'Deviation', severity: 'Major', link: '/deviations?id=DEV-OSD-2026-014', icon: 'FileWarning' },
      { id: 5, title: 'Supplier COA missing for MAT-PARA-042', type: 'Material', severity: 'High', link: '/materials?id=MAT-PARA-042', icon: 'Package' },
      { id: 6, title: 'SOP version mismatch in checklist execution', type: 'Documentation', severity: 'Minor', link: '/deviations?id=DEV-OSD-2026-013', icon: 'FileText' }
    ],
    'AREA-OSD': [
      { id: 1, title: 'Checklist failure: Line clearance incomplete', type: 'Deviation', severity: 'Major', link: '/deviations?id=DEV-OSD-2026-014', icon: 'FileWarning' },
      { id: 2, title: 'SOP version mismatch in checklist execution', type: 'Documentation', severity: 'Minor', link: '/deviations?id=DEV-OSD-2026-013', icon: 'FileText' },
      { id: 3, title: 'Tablet coating parameters drift detected', type: 'Process', severity: 'High', link: '/deviations?id=DEV-OSD-2026-011', icon: 'AlertTriangle' }
    ],
    'AREA-STER': [
      { id: 1, title: 'HVAC differential pressure excursion – Sterile Suite', type: 'Utilities', severity: 'Critical', link: '/assets?id=AHU-STER-001', icon: 'ThermometerSun' },
      { id: 2, title: 'Particle count alert in Grade A zone', type: 'Environmental', severity: 'High', link: '/assets?id=LAF-STER-002', icon: 'AlertTriangle' }
    ],
    'AREA-WH': [
      { id: 1, title: 'Warehouse cold room temp excursion', type: 'Storage', severity: 'Critical', link: '/assets?id=CR-WH-001', icon: 'ThermometerSun' },
      { id: 2, title: 'Supplier COA missing for MAT-PARA-042', type: 'Material', severity: 'High', link: '/materials?id=MAT-PARA-042', icon: 'Package' }
    ],
    'AREA-QC': [
      { id: 1, title: 'Calibration overdue – EQ-BAL-017', type: 'Equipment', severity: 'High', link: '/assets?id=EQ-BAL-017', icon: 'Scale' },
      { id: 2, title: 'Out of trend result - Dissolution test', type: 'Testing', severity: 'Major', link: '/deviations?id=DEV-QC-2026-012', icon: 'FileWarning' }
    ]
  },
  'PLANT-B': {
    'all': [
      { id: 1, title: 'HVAC filter replacement overdue – Sterile Suite B', type: 'Utilities', severity: 'Critical', link: '/assets?id=AHU-STER-B-001', icon: 'ThermometerSun' },
      { id: 2, title: 'Granulator PM overdue – EQ-GRAN-B-001', type: 'Equipment', severity: 'High', link: '/assets?id=EQ-GRAN-B-001', icon: 'Settings' },
      { id: 3, title: 'Cold storage capacity at 95%', type: 'Storage', severity: 'High', link: '/assets?id=CR-WH-B-001', icon: 'Package' },
      { id: 4, title: 'Pending supplier qualification audit', type: 'Supplier', severity: 'Major', link: '/audits/supplier', icon: 'ClipboardCheck' },
      { id: 5, title: 'Training gap identified - New SOPs', type: 'Training', severity: 'High', link: '/documents?tab=training', icon: 'Users' }
    ],
    'AREA-OSD-B': [
      { id: 1, title: 'Granulator PM overdue – EQ-GRAN-B-001', type: 'Equipment', severity: 'High', link: '/assets?id=EQ-GRAN-B-001', icon: 'Settings' },
      { id: 2, title: 'Yield deviation in blending process', type: 'Process', severity: 'Major', link: '/deviations?id=DEV-OSD-B-2026-003', icon: 'FileWarning' },
      { id: 3, title: 'Training gap identified - Compression SOPs', type: 'Training', severity: 'High', link: '/documents?tab=training', icon: 'Users' }
    ],
    'AREA-STER-B': [
      { id: 1, title: 'HVAC filter replacement overdue – Sterile Suite B', type: 'Utilities', severity: 'Critical', link: '/assets?id=AHU-STER-B-001', icon: 'ThermometerSun' },
      { id: 2, title: 'Environmental monitoring trend alert', type: 'Environmental', severity: 'High', link: '/assets?id=EM-STER-B-001', icon: 'AlertTriangle' }
    ],
    'AREA-WH-B': [
      { id: 1, title: 'Cold storage capacity at 95%', type: 'Storage', severity: 'High', link: '/assets?id=CR-WH-B-001', icon: 'Package' },
      { id: 2, title: 'Material quarantine area overflow', type: 'Storage', severity: 'Major', link: '/materials', icon: 'Package' }
    ],
    'AREA-QC-B': [
      { id: 1, title: 'Stability chamber calibration due', type: 'Equipment', severity: 'High', link: '/assets?id=STAB-CHAMBER-B-01', icon: 'Scale' },
      { id: 2, title: 'Reference standard expiring soon', type: 'Material', severity: 'Major', link: '/materials?tab=reference', icon: 'Package' }
    ]
  }
};

// Helper function to get at-risk items
export const getAtRiskItems = (plantId, areaId) => {
  const area = areaId === 'all' ? 'all' : areaId;
  const plantData = atRiskItemsByFilter[plantId];
  if (!plantData) return atRiskItemsByFilter['PLANT-A']['all'];
  
  return plantData[area] || plantData['all'];
};

// ============================================================
// COMPLIANCE TREND DATA BY FILTER
// ============================================================

export const complianceTrendByFilter = {
  'PLANT-A': {
    'all': {
      '7days': [
        { day: 'Mon', score: 91 },
        { day: 'Tue', score: 92 },
        { day: 'Wed', score: 90 },
        { day: 'Thu', score: 93 },
        { day: 'Fri', score: 91 },
        { day: 'Sat', score: 92 },
        { day: 'Sun', score: 91 }
      ],
      '30days': [
        { week: 'W1', score: 88 },
        { week: 'W2', score: 90 },
        { week: 'W3', score: 89 },
        { week: 'W4', score: 92 }
      ],
      'quarter': [
        { month: 'Oct', score: 88 },
        { month: 'Nov', score: 90 },
        { month: 'Dec', score: 89 },
        { month: 'Jan', score: 92 }
      ]
    }
  },
  'PLANT-B': {
    'all': {
      '7days': [
        { day: 'Mon', score: 85 },
        { day: 'Tue', score: 86 },
        { day: 'Wed', score: 84 },
        { day: 'Thu', score: 87 },
        { day: 'Fri', score: 85 },
        { day: 'Sat', score: 86 },
        { day: 'Sun', score: 85 }
      ],
      '30days': [
        { week: 'W1', score: 82 },
        { week: 'W2', score: 84 },
        { week: 'W3', score: 83 },
        { week: 'W4', score: 85 }
      ],
      'quarter': [
        { month: 'Oct', score: 80 },
        { month: 'Nov', score: 82 },
        { month: 'Dec', score: 83 },
        { month: 'Jan', score: 85 }
      ]
    }
  }
};

// Helper to get trend data
export const getComplianceTrend = (plantId, areaId, timeRange) => {
  const plantData = complianceTrendByFilter[plantId] || complianceTrendByFilter['PLANT-A'];
  const areaData = plantData[areaId] || plantData['all'];
  return areaData[timeRange] || areaData['30days'];
};

// ============================================================
// DEVIATIONS BY CATEGORY
// ============================================================

export const deviationsByCategoryByFilter = {
  'PLANT-A': {
    'all': [
      { name: 'Documentation', value: 5 },
      { name: 'Equipment', value: 4 },
      { name: 'Process', value: 3 },
      { name: 'Environmental', value: 2 }
    ],
    'AREA-OSD': [
      { name: 'Documentation', value: 3 },
      { name: 'Process', value: 2 },
      { name: 'Equipment', value: 1 },
      { name: 'Environmental', value: 0 }
    ],
    'AREA-STER': [
      { name: 'Environmental', value: 2 },
      { name: 'Equipment', value: 2 },
      { name: 'Process', value: 0 },
      { name: 'Documentation', value: 0 }
    ],
    'AREA-WH': [
      { name: 'Environmental', value: 1 },
      { name: 'Documentation', value: 1 },
      { name: 'Equipment', value: 0 },
      { name: 'Process', value: 0 }
    ],
    'AREA-QC': [
      { name: 'Equipment', value: 1 },
      { name: 'Documentation', value: 1 },
      { name: 'Process', value: 0 },
      { name: 'Environmental', value: 0 }
    ]
  },
  'PLANT-B': {
    'all': [
      { name: 'Equipment', value: 6 },
      { name: 'Process', value: 5 },
      { name: 'Documentation', value: 3 },
      { name: 'Environmental', value: 2 }
    ],
    'AREA-OSD-B': [
      { name: 'Process', value: 3 },
      { name: 'Equipment', value: 2 },
      { name: 'Documentation', value: 2 },
      { name: 'Environmental', value: 0 }
    ],
    'AREA-STER-B': [
      { name: 'Environmental', value: 2 },
      { name: 'Equipment', value: 2 },
      { name: 'Process', value: 1 },
      { name: 'Documentation', value: 0 }
    ],
    'AREA-WH-B': [
      { name: 'Environmental', value: 1 },
      { name: 'Equipment', value: 1 },
      { name: 'Documentation', value: 1 },
      { name: 'Process', value: 0 }
    ],
    'AREA-QC-B': [
      { name: 'Equipment', value: 1 },
      { name: 'Process', value: 1 },
      { name: 'Documentation', value: 0 },
      { name: 'Environmental', value: 0 }
    ]
  }
};

// Helper to get deviations by category
export const getDeviationsByCategory = (plantId, areaId) => {
  const area = areaId === 'all' ? 'all' : areaId;
  const plantData = deviationsByCategoryByFilter[plantId];
  if (!plantData) return deviationsByCategoryByFilter['PLANT-A']['all'];
  
  return plantData[area] || plantData['all'];
};

// ============================================================
// DEFAULT EXPORTS (for backwards compatibility)
// ============================================================

// Default dashboard metrics (PLANT-A, all areas, 30 days)
export const dashboardMetrics = dashboardMetricsByFilter['PLANT-A']['all']['30days'];

// Requirements Library (Schedule-M + WHO-GMP)
export const requirements = [
  { id: 'SM-PQS-001', title: 'Pharmaceutical Quality System & Management Responsibility', category: 'Quality System', scheduleMRef: 'Schedule M, Part I, 2.1', whoRef: 'WHO TRS 986, Annex 2', evidenceExpected: ['QMS Manual', 'Management Review Records', 'Quality Policy'], coverage: 92 },
  { id: 'SM-QRM-001', title: 'Quality Risk Management', category: 'Quality System', scheduleMRef: 'Schedule M, Part I, 2.2', whoRef: 'WHO TRS 981, Annex 2', evidenceExpected: ['Risk Assessments', 'FMEA Records', 'Risk Register'], coverage: 88 },
  { id: 'SM-DOC-001', title: 'Document Control, Versioning & Retention', category: 'Documentation', scheduleMRef: 'Schedule M, Part I, 9', whoRef: 'WHO TRS 986, Annex 2', evidenceExpected: ['Document Master List', 'Version History', 'Retention Policy'], coverage: 95 },
  { id: 'SM-DI-001', title: 'Data Integrity & Audit Trails', category: 'Data Integrity', scheduleMRef: 'Schedule M, Part I, 9.1', whoRef: 'WHO Guidance on Data Integrity', evidenceExpected: ['Audit Trail Reports', 'Access Controls', 'Backup Records'], coverage: 90 },
  { id: 'SM-SI-001', title: 'Self-Inspection Program (Annual)', category: 'Audits', scheduleMRef: 'Schedule M, Part I, 17', whoRef: 'WHO TRS 986, Annex 2', evidenceExpected: ['Self-Inspection Schedule', 'Audit Reports', 'CAPA Records'], coverage: 85 },
  { id: 'SM-DEV-001', title: 'Deviation Reporting & Investigation', category: 'Quality Events', scheduleMRef: 'Schedule M, Part I, 16', whoRef: 'WHO TRS 986, Annex 2', evidenceExpected: ['Deviation Forms', 'Investigation Reports', 'Root Cause Analysis'], coverage: 91 },
  { id: 'SM-CAPA-001', title: 'CAPA & Effectiveness Checks', category: 'Quality Events', scheduleMRef: 'Schedule M, Part I, 16.2', whoRef: 'WHO TRS 986, Annex 2', evidenceExpected: ['CAPA Forms', 'Effectiveness Check Records', 'Trend Analysis'], coverage: 87 },
  { id: 'SM-CC-001', title: 'Change Control with Risk-Based Routing', category: 'Change Control', scheduleMRef: 'Schedule M, Part I, 15', whoRef: 'WHO TRS 986, Annex 2', evidenceExpected: ['Change Control Forms', 'Risk Assessments', 'Approval Records'], coverage: 89 },
  { id: 'SM-PQR-001', title: 'Product Quality Review (Annual/Rolling)', category: 'PQR', scheduleMRef: 'Schedule M, Part I, 18', whoRef: 'WHO TRS 986, Annex 2', evidenceExpected: ['PQR Reports', 'Trend Analysis', 'Action Items'], coverage: 82 },
  { id: 'SM-EQP-001', title: 'Equipment Qualification/Calibration/Maintenance', category: 'Equipment', scheduleMRef: 'Schedule M, Part I, 5', whoRef: 'WHO TRS 986, Annex 2', evidenceExpected: ['Calibration Records', 'PM Records', 'Qualification Protocols'], coverage: 86 },
  { id: 'SM-HVAC-001', title: 'HVAC Qualification & Monitoring', category: 'Utilities', scheduleMRef: 'Schedule M, Part I, 4.3', whoRef: 'WHO TRS 961, Annex 5', evidenceExpected: ['HVAC Qualification', 'Environmental Monitoring', 'Differential Pressure Logs'], coverage: 84 },
  { id: 'SM-SAN-001', title: 'Cleaning & Sanitation Records', category: 'Sanitation', scheduleMRef: 'Schedule M, Part I, 6', whoRef: 'WHO TRS 986, Annex 2', evidenceExpected: ['Cleaning Records', 'Sanitation SOPs', 'Verification Records'], coverage: 93 },
  { id: 'SM-MAT-001', title: 'Materials Receipt/Quarantine/Release', category: 'Materials', scheduleMRef: 'Schedule M, Part I, 7', whoRef: 'WHO TRS 986, Annex 2', evidenceExpected: ['GRN Records', 'Quarantine Logs', 'Release Records'], coverage: 90 },
  { id: 'SM-SUP-001', title: 'Supplier Approval & Audits', category: 'Suppliers', scheduleMRef: 'Schedule M, Part I, 7.1', whoRef: 'WHO TRS 986, Annex 2', evidenceExpected: ['Supplier Qualification', 'Audit Reports', 'Approved Supplier List'], coverage: 81 },
  { id: 'SM-COMP-001', title: 'Complaints Investigation & Batch Linkage', category: 'Complaints', scheduleMRef: 'Schedule M, Part I, 19', whoRef: 'WHO TRS 986, Annex 2', evidenceExpected: ['Complaint Forms', 'Investigation Reports', 'Batch Traceability'], coverage: 88 },
  { id: 'SM-RECALL-001', title: 'Recall Readiness & Distribution Traceability', category: 'Recall', scheduleMRef: 'Schedule M, Part I, 20', whoRef: 'WHO TRS 986, Annex 2', evidenceExpected: ['Recall SOP', 'Mock Recall Records', 'Distribution Records'], coverage: 79 },
  { id: 'SM-TRN-001', title: 'Training & Competency Matrix', category: 'Training', scheduleMRef: 'Schedule M, Part I, 3', whoRef: 'WHO TRS 986, Annex 2', evidenceExpected: ['Training Records', 'Competency Assessments', 'Training Matrix'], coverage: 94 },
  { id: 'SM-SMF-001', title: 'Site Master File Maintenance', category: 'Documentation', scheduleMRef: 'Schedule M, Part I, 21', whoRef: 'WHO TRS 961, Annex 4', evidenceExpected: ['Site Master File', 'Update Records', 'Review History'], coverage: 91 },
  { id: 'SM-CSV-001', title: 'Computer System Validation (GxP)', category: 'Validation', scheduleMRef: 'Schedule M, Part I, 12', whoRef: 'WHO TRS 996, Annex 5', evidenceExpected: ['Validation Protocols', 'Test Scripts', 'Validation Reports'], coverage: 83 },
  { id: 'SM-AUD-001', title: 'Audit Pack Generation & Evidence Indexing', category: 'Audits', scheduleMRef: 'Schedule M, Part I, 17.2', whoRef: 'WHO TRS 986, Annex 2', evidenceExpected: ['Audit Packs', 'Evidence Index', 'Hash Verification'], coverage: 86 },
  { id: 'WHO-TRS-HVAC', title: 'WHO GMP Guidance - HVAC Systems', category: 'WHO Alignment', scheduleMRef: 'Schedule M, Part I, 4.3', whoRef: 'WHO TRS 961, Annex 5', evidenceExpected: ['HVAC Design Qualification', 'Air Handling Records'], coverage: 85 },
  { id: 'WHO-TRS-WATER', title: 'WHO GMP Guidance - Water Systems', category: 'WHO Alignment', scheduleMRef: 'Schedule M, Part I, 4.4', whoRef: 'WHO TRS 929, Annex 2', evidenceExpected: ['Water System Qualification', 'Testing Records'], coverage: 87 },
  { id: 'WHO-TRS-VALIDATION', title: 'WHO GMP Guidance - Validation Approach', category: 'WHO Alignment', scheduleMRef: 'Schedule M, Part I, 12', whoRef: 'WHO TRS 937, Annex 4', evidenceExpected: ['VMP', 'Validation Reports'], coverage: 84 },
  { id: 'WHO-TRS-PACKAGING', title: 'WHO GMP Guidance - Packaging Controls', category: 'WHO Alignment', scheduleMRef: 'Schedule M, Part I, 11', whoRef: 'WHO TRS 986, Annex 2', evidenceExpected: ['Packaging Records', 'Line Clearance'], coverage: 90 }
];

// Tasks
export const tasks = [
  { id: 'TASK-001', name: 'Daily GMP Round – Block A – Oral Solid – Shift 1', area: 'AREA-OSD', frequency: 'Daily', dueDate: '2026-01-18T08:00:00', owner: 'Rajesh Kumar', role: 'OPERATOR', criticality: 'High', status: 'Pending', checklistId: 'CL-001' },
  { id: 'TASK-002', name: 'Line Clearance – Compression Area', area: 'AREA-OSD', frequency: 'Per Batch', dueDate: '2026-01-18T09:30:00', owner: 'Priya Sharma', role: 'PROD_SUP', criticality: 'Critical', status: 'In Progress', checklistId: 'CL-002' },
  { id: 'TASK-003', name: 'Environmental Monitoring – Sterile Suite', area: 'AREA-STER', frequency: 'Daily', dueDate: '2026-01-18T07:00:00', owner: 'Amit Patel', role: 'QC_ANALYST', criticality: 'Critical', status: 'Completed', checklistId: 'CL-003' },
  { id: 'TASK-004', name: 'Equipment Calibration Check – Balance EQ-BAL-017', area: 'AREA-QC', frequency: 'Weekly', dueDate: '2026-01-15T10:00:00', owner: 'Suresh Reddy', role: 'ENGINEERING', criticality: 'High', status: 'Overdue', checklistId: 'CL-004' },
  { id: 'TASK-005', name: 'Warehouse Temperature Monitoring', area: 'AREA-WH', frequency: 'Twice Daily', dueDate: '2026-01-18T14:00:00', owner: 'Vikram Singh', role: 'WAREHOUSE', criticality: 'Medium', status: 'Pending', checklistId: 'CL-005' },
  { id: 'TASK-006', name: 'HVAC Differential Pressure Check – Sterile Suite', area: 'AREA-STER', frequency: 'Daily', dueDate: '2026-01-18T06:00:00', owner: 'Kavitha Nair', role: 'ENGINEERING', criticality: 'Critical', status: 'Pending', checklistId: 'CL-006' },
  { id: 'TASK-007', name: 'Raw Material Sampling – MAT-PARA-042', area: 'AREA-QC', frequency: 'Per Receipt', dueDate: '2026-01-18T11:00:00', owner: 'Deepak Joshi', role: 'QC_ANALYST', criticality: 'High', status: 'Pending', checklistId: 'CL-007' },
  { id: 'TASK-008', name: 'Weekly Equipment PM – Tablet Press', area: 'AREA-OSD', frequency: 'Weekly', dueDate: '2026-01-20T08:00:00', owner: 'Manoj Verma', role: 'ENGINEERING', criticality: 'High', status: 'Upcoming', checklistId: 'CL-008' },
  { id: 'TASK-009', name: 'Monthly Self-Inspection Prep', area: 'AREA-OSD', frequency: 'Monthly', dueDate: '2026-01-25T09:00:00', owner: 'Dr. Anita Kulkarni', role: 'QA_HEAD', criticality: 'High', status: 'Upcoming', checklistId: 'CL-009' },
  { id: 'TASK-010', name: 'Batch Record Review – BATCH-PCM-0126-0042', area: 'AREA-OSD', frequency: 'Per Batch', dueDate: '2026-01-18T16:00:00', owner: 'Sneha Desai', role: 'QA_OFFICER', criticality: 'Critical', status: 'Pending', checklistId: 'CL-010' },
  { id: 'TASK-011', name: 'Water System Sampling', area: 'AREA-QC', frequency: 'Daily', dueDate: '2026-01-18T07:30:00', owner: 'Rahul Mehta', role: 'QC_ANALYST', criticality: 'High', status: 'Completed', checklistId: 'CL-011' },
  { id: 'TASK-012', name: 'Cleaning Verification – Mixing Area', area: 'AREA-OSD', frequency: 'Per Changeover', dueDate: '2026-01-18T12:00:00', owner: 'Sunita Rao', role: 'OPERATOR', criticality: 'Critical', status: 'Pending', checklistId: 'CL-012' },
  { id: 'TASK-013', name: 'Supplier COA Review – MAT-PARA-042', area: 'AREA-QC', frequency: 'Per Receipt', dueDate: '2026-01-17T14:00:00', owner: 'Dr. Ravi Shankar', role: 'QA_OFFICER', criticality: 'High', status: 'Overdue', checklistId: 'CL-013' },
  { id: 'TASK-014', name: 'Training Assessment – New SOPs', area: 'AREA-OSD', frequency: 'As Required', dueDate: '2026-01-19T10:00:00', owner: 'Geeta Iyer', role: 'QA_OFFICER', criticality: 'Medium', status: 'Upcoming', checklistId: 'CL-014' },
  { id: 'TASK-015', name: 'Cold Room Temperature Verification', area: 'AREA-WH', frequency: 'Twice Daily', dueDate: '2026-01-18T06:30:00', owner: 'Prakash Nair', role: 'WAREHOUSE', criticality: 'Critical', status: 'Pending', checklistId: 'CL-015' }
];

// Checklist Templates
export const checklistTemplates = [
  { id: 'CL-001', name: 'Daily GMP Round Checklist', version: '3.2', status: 'Effective', effectiveDate: '2025-10-01', nextReview: '2026-10-01', questions: 15, category: 'GMP Rounds', linkedReqs: ['SM-SAN-001', 'SM-DI-001'] },
  { id: 'CL-002', name: 'Line Clearance Checklist', version: '2.5', status: 'Effective', effectiveDate: '2025-08-15', nextReview: '2026-08-15', questions: 22, category: 'Production', linkedReqs: ['SM-DOC-001', 'SM-SAN-001'] },
  { id: 'CL-003', name: 'Environmental Monitoring Checklist', version: '4.0', status: 'Effective', effectiveDate: '2025-11-01', nextReview: '2026-11-01', questions: 18, category: 'QC', linkedReqs: ['SM-HVAC-001', 'WHO-TRS-HVAC'] },
  { id: 'CL-004', name: 'Equipment Calibration Checklist', version: '2.1', status: 'Effective', effectiveDate: '2025-09-01', nextReview: '2026-09-01', questions: 12, category: 'Calibration', linkedReqs: ['SM-EQP-001'] },
  { id: 'CL-005', name: 'Warehouse Temperature Monitoring', version: '1.8', status: 'Effective', effectiveDate: '2025-07-01', nextReview: '2026-07-01', questions: 8, category: 'Storage', linkedReqs: ['SM-MAT-001'] },
  { id: 'CL-006', name: 'HVAC Differential Pressure Checklist', version: '3.0', status: 'Effective', effectiveDate: '2025-10-15', nextReview: '2026-10-15', questions: 10, category: 'Utilities', linkedReqs: ['SM-HVAC-001', 'WHO-TRS-HVAC'] },
  { id: 'CL-007', name: 'Raw Material Sampling Checklist', version: '2.3', status: 'Effective', effectiveDate: '2025-06-01', nextReview: '2026-06-01', questions: 14, category: 'QC', linkedReqs: ['SM-MAT-001', 'SM-SUP-001'] },
  { id: 'CL-008', name: 'Equipment PM Checklist – Tablet Press', version: '4.2', status: 'Effective', effectiveDate: '2025-12-01', nextReview: '2026-12-01', questions: 25, category: 'Maintenance', linkedReqs: ['SM-EQP-001'] }
];

// Checklist Executions
export const checklistExecutions = [
  { id: 'EXEC-001', checklistId: 'CL-001', taskId: 'TASK-001', executedBy: 'Rajesh Kumar', executedAt: '2026-01-17T08:15:00', status: 'Passed', passRate: 100, deviationCreated: null },
  { id: 'EXEC-002', checklistId: 'CL-002', taskId: 'TASK-002', executedBy: 'Priya Sharma', executedAt: '2026-01-17T10:00:00', status: 'Failed', passRate: 91, deviationCreated: 'DEV-OSD-2026-013' },
  { id: 'EXEC-003', checklistId: 'CL-003', taskId: 'TASK-003', executedBy: 'Amit Patel', executedAt: '2026-01-18T07:30:00', status: 'Passed', passRate: 100, deviationCreated: null },
  { id: 'EXEC-004', checklistId: 'CL-006', taskId: 'TASK-006', executedBy: 'Kavitha Nair', executedAt: '2026-01-17T06:30:00', status: 'Failed', passRate: 80, deviationCreated: 'DEV-STER-2026-008' }
];

// Deviations
export const deviations = [
  { id: 'DEV-OSD-2026-014', title: 'Line clearance incomplete - residual material found', area: 'AREA-OSD', severity: 'Major', status: 'Open', reportedBy: 'Priya Sharma', reportedAt: '2026-01-18T09:45:00', batch: 'BATCH-PCM-0126-0042', equipment: 'EQ-MIX-022', room: 'RM-OSD-101', capaId: null, rootCause: null, linkedReqs: ['SM-DEV-001', 'SM-SAN-001'] },
  { id: 'DEV-OSD-2026-013', title: 'SOP version mismatch during batch execution', area: 'AREA-OSD', severity: 'Minor', status: 'Under Investigation', reportedBy: 'Rajesh Kumar', reportedAt: '2026-01-17T14:20:00', batch: 'BATCH-PCM-0126-0041', equipment: null, room: 'RM-OSD-102', capaId: 'CAPA-2026-008', rootCause: 'Training gap', linkedReqs: ['SM-DEV-001', 'SM-DOC-001', 'SM-TRN-001'] },
  { id: 'DEV-STER-2026-008', title: 'HVAC differential pressure excursion', area: 'AREA-STER', severity: 'Critical', status: 'Under Investigation', reportedBy: 'Kavitha Nair', reportedAt: '2026-01-17T06:45:00', batch: null, equipment: 'AHU-STER-001', room: 'RM-STER-001', capaId: 'CAPA-2026-007', rootCause: 'Filter degradation', linkedReqs: ['SM-DEV-001', 'SM-HVAC-001', 'WHO-TRS-HVAC'] },
  { id: 'DEV-WH-2026-005', title: 'Cold room temperature excursion (2-8°C range)', area: 'AREA-WH', severity: 'Critical', status: 'CAPA Assigned', reportedBy: 'Vikram Singh', reportedAt: '2026-01-15T22:30:00', batch: null, equipment: 'CR-WH-001', room: 'RM-WH-COLD-01', capaId: 'CAPA-2026-006', rootCause: 'Compressor failure', linkedReqs: ['SM-DEV-001', 'SM-MAT-001'] },
  { id: 'DEV-QC-2026-012', title: 'Out of trend result - Dissolution test', area: 'AREA-QC', severity: 'Major', status: 'Under Investigation', reportedBy: 'Deepak Joshi', reportedAt: '2026-01-16T15:00:00', batch: 'BATCH-PCM-0126-0040', equipment: 'EQ-DISS-003', room: 'RM-QC-101', capaId: null, rootCause: null, linkedReqs: ['SM-DEV-001'] },
  { id: 'DEV-OSD-2026-011', title: 'Yield deviation >2% - Coating process', area: 'AREA-OSD', severity: 'Minor', status: 'Closed', reportedBy: 'Manoj Verma', reportedAt: '2026-01-14T16:30:00', batch: 'BATCH-PCM-0126-0039', equipment: 'EQ-COAT-001', room: 'RM-OSD-105', capaId: 'CAPA-2026-005', rootCause: 'Process parameter drift', linkedReqs: ['SM-DEV-001'] },
  { id: 'DEV-OSD-2026-010', title: 'Missing batch record entry', area: 'AREA-OSD', severity: 'Minor', status: 'Closed', reportedBy: 'Sneha Desai', reportedAt: '2026-01-13T11:00:00', batch: 'BATCH-PCM-0126-0038', equipment: null, room: null, capaId: 'CAPA-2026-004', rootCause: 'Human error', linkedReqs: ['SM-DEV-001', 'SM-DI-001'] },
  { id: 'DEV-STER-2026-007', title: 'Particle count exceedance - Grade A zone', area: 'AREA-STER', severity: 'Critical', status: 'Closed', reportedBy: 'Amit Patel', reportedAt: '2026-01-10T09:15:00', batch: 'BATCH-INJ-0126-0012', equipment: 'LAF-STER-002', room: 'RM-STER-002', capaId: 'CAPA-2026-003', rootCause: 'HEPA filter integrity', linkedReqs: ['SM-DEV-001', 'SM-HVAC-001'] },
  { id: 'DEV-QC-2026-009', title: 'Calibration sticker missing on balance', area: 'AREA-QC', severity: 'Minor', status: 'Open', reportedBy: 'Suresh Reddy', reportedAt: '2026-01-17T10:30:00', batch: null, equipment: 'EQ-BAL-017', room: 'RM-QC-102', capaId: null, rootCause: null, linkedReqs: ['SM-DEV-001', 'SM-EQP-001'] },
  { id: 'DEV-WH-2026-004', title: 'Supplier COA discrepancy - MAT-PARA-042', area: 'AREA-WH', severity: 'Major', status: 'Under Investigation', reportedBy: 'Dr. Ravi Shankar', reportedAt: '2026-01-16T14:45:00', batch: null, equipment: null, room: null, capaId: null, rootCause: null, linkedReqs: ['SM-DEV-001', 'SM-MAT-001', 'SM-SUP-001'] },
  { id: 'DEV-OSD-2026-009', title: 'Equipment malfunction during granulation', area: 'AREA-OSD', severity: 'Major', status: 'CAPA Assigned', reportedBy: 'Rajesh Kumar', reportedAt: '2026-01-12T08:45:00', batch: 'BATCH-PCM-0126-0037', equipment: 'EQ-GRAN-001', room: 'RM-OSD-103', capaId: 'CAPA-2026-002', rootCause: 'Mechanical wear', linkedReqs: ['SM-DEV-001', 'SM-EQP-001'] },
  { id: 'DEV-OSD-2026-008', title: 'Incorrect raw material dispensed', area: 'AREA-OSD', severity: 'Critical', status: 'Closed', reportedBy: 'Sunita Rao', reportedAt: '2026-01-08T10:00:00', batch: 'BATCH-PCM-0126-0035', equipment: null, room: 'RM-OSD-DISP-01', capaId: 'CAPA-2026-001', rootCause: 'Label similarity', linkedReqs: ['SM-DEV-001', 'SM-MAT-001'] },
  { id: 'DEV-QC-2026-006', title: 'Stability sample storage deviation', area: 'AREA-QC', severity: 'Minor', status: 'Closed', reportedBy: 'Rahul Mehta', reportedAt: '2026-01-05T16:00:00', batch: 'BATCH-PCM-0125-0180', equipment: 'STAB-CHAMBER-02', room: 'RM-QC-STAB', capaId: null, rootCause: 'Power fluctuation', linkedReqs: ['SM-DEV-001'] },
  { id: 'DEV-STER-2026-005', title: 'Gowning procedure violation', area: 'AREA-STER', severity: 'Major', status: 'Closed', reportedBy: 'Dr. Anita Kulkarni', reportedAt: '2026-01-03T07:30:00', batch: null, equipment: null, room: 'RM-STER-GOWN', capaId: 'CAPA-2025-048', rootCause: 'Training deficiency', linkedReqs: ['SM-DEV-001', 'SM-TRN-001'] }
];

// CAPAs
export const capas = [
  { id: 'CAPA-2026-009', title: 'Implement barcode verification for line clearance', deviationId: 'DEV-OSD-2026-014', status: 'Draft', priority: 'High', owner: 'Dr. Anita Kulkarni', dueDate: '2026-02-15', effectivenessCheck: '30 days', progress: 0, linkedReqs: ['SM-CAPA-001', 'SM-SAN-001'] },
  { id: 'CAPA-2026-008', title: 'SOP training reinforcement program', deviationId: 'DEV-OSD-2026-013', status: 'In Progress', priority: 'Medium', owner: 'Geeta Iyer', dueDate: '2026-02-01', effectivenessCheck: '60 days', progress: 45, linkedReqs: ['SM-CAPA-001', 'SM-TRN-001'] },
  { id: 'CAPA-2026-007', title: 'HVAC filter replacement and requalification', deviationId: 'DEV-STER-2026-008', status: 'In Progress', priority: 'Critical', owner: 'Kavitha Nair', dueDate: '2026-01-25', effectivenessCheck: '30 days', progress: 60, linkedReqs: ['SM-CAPA-001', 'SM-HVAC-001', 'WHO-TRS-HVAC'] },
  { id: 'CAPA-2026-006', title: 'Cold room backup system installation', deviationId: 'DEV-WH-2026-005', status: 'In Progress', priority: 'Critical', owner: 'Prakash Nair', dueDate: '2026-02-10', effectivenessCheck: '90 days', progress: 35, linkedReqs: ['SM-CAPA-001', 'SM-MAT-001'] },
  { id: 'CAPA-2026-005', title: 'Coating process parameter optimization', deviationId: 'DEV-OSD-2026-011', status: 'Effectiveness Check', priority: 'Low', owner: 'Manoj Verma', dueDate: '2026-01-10', effectivenessCheck: '30 days', progress: 90, linkedReqs: ['SM-CAPA-001'] },
  { id: 'CAPA-2026-004', title: 'Electronic batch record implementation', deviationId: 'DEV-OSD-2026-010', status: 'Closed', priority: 'Medium', owner: 'Sneha Desai', dueDate: '2025-12-31', effectivenessCheck: '60 days', progress: 100, linkedReqs: ['SM-CAPA-001', 'SM-DI-001'] },
  { id: 'CAPA-2026-003', title: 'HEPA filter integrity testing enhancement', deviationId: 'DEV-STER-2026-007', status: 'Closed', priority: 'Critical', owner: 'Amit Patel', dueDate: '2025-12-20', effectivenessCheck: '30 days', progress: 100, linkedReqs: ['SM-CAPA-001', 'SM-HVAC-001'] },
  { id: 'CAPA-2026-002', title: 'Granulator preventive maintenance schedule revision', deviationId: 'DEV-OSD-2026-009', status: 'Implemented', priority: 'High', owner: 'Suresh Reddy', dueDate: '2026-01-20', effectivenessCheck: '60 days', progress: 80, linkedReqs: ['SM-CAPA-001', 'SM-EQP-001'] },
  { id: 'CAPA-2026-001', title: 'Raw material label redesign with unique identifiers', deviationId: 'DEV-OSD-2026-008', status: 'Closed', priority: 'Critical', owner: 'Dr. Ravi Shankar', dueDate: '2025-12-15', effectivenessCheck: '90 days', progress: 100, linkedReqs: ['SM-CAPA-001', 'SM-MAT-001'] },
  { id: 'CAPA-2025-048', title: 'Gowning qualification retraining', deviationId: 'DEV-STER-2026-005', status: 'Closed', priority: 'High', owner: 'Dr. Anita Kulkarni', dueDate: '2025-12-10', effectivenessCheck: '30 days', progress: 100, linkedReqs: ['SM-CAPA-001', 'SM-TRN-001'] }
];

// Change Controls
export const changeControls = [
  { id: 'CC-STER-2026-003', title: 'Replace HVAC HEPA filter specification in Sterile Suite', type: 'Utility', status: 'Pending QA Approval', riskLevel: 'High', initiator: 'Kavitha Nair', initiatedAt: '2026-01-15T10:00:00', targetDate: '2026-02-28', impactedSOPs: ['SOP-HVAC-001', 'SOP-EM-003'], impactedTraining: ['TRN-HVAC-001'], linkedReqs: ['SM-CC-001', 'SM-HVAC-001', 'WHO-TRS-HVAC'] },
  { id: 'CC-OSD-2026-002', title: 'Update tablet compression parameters for PCM 500mg', type: 'Process', status: 'Implementation', riskLevel: 'Medium', initiator: 'Priya Sharma', initiatedAt: '2026-01-10T14:30:00', targetDate: '2026-02-15', impactedSOPs: ['SOP-COMP-002'], impactedTraining: ['TRN-COMP-001'], linkedReqs: ['SM-CC-001'] },
  { id: 'CC-QC-2026-001', title: 'New dissolution apparatus procurement', type: 'Equipment', status: 'Approved', riskLevel: 'Low', initiator: 'Deepak Joshi', initiatedAt: '2026-01-05T09:00:00', targetDate: '2026-03-01', impactedSOPs: ['SOP-DISS-001'], impactedTraining: ['TRN-DISS-001'], linkedReqs: ['SM-CC-001', 'SM-EQP-001'] },
  { id: 'CC-WH-2026-001', title: 'Cold storage expansion project', type: 'Facility', status: 'Risk Assessment', riskLevel: 'High', initiator: 'Prakash Nair', initiatedAt: '2026-01-12T11:00:00', targetDate: '2026-06-30', impactedSOPs: ['SOP-COLD-001', 'SOP-MAT-REC-001'], impactedTraining: [], linkedReqs: ['SM-CC-001', 'SM-MAT-001'] },
  { id: 'CC-OSD-2026-001', title: 'Blister packaging line upgrade', type: 'Equipment', status: 'Closed', riskLevel: 'Medium', initiator: 'Manoj Verma', initiatedAt: '2025-12-01T08:00:00', targetDate: '2026-01-15', impactedSOPs: ['SOP-PACK-001'], impactedTraining: ['TRN-PACK-002'], linkedReqs: ['SM-CC-001', 'WHO-TRS-PACKAGING'] },
  { id: 'CC-SUP-2026-001', title: 'Add alternate supplier for Paracetamol API', type: 'Supplier', status: 'Pending QA Approval', riskLevel: 'Medium', initiator: 'Dr. Ravi Shankar', initiatedAt: '2026-01-14T15:00:00', targetDate: '2026-03-15', impactedSOPs: ['SOP-SUP-QUAL-001'], impactedTraining: [], linkedReqs: ['SM-CC-001', 'SM-SUP-001'] }
];

// Equipment/Assets
export const equipment = [
  { id: 'EQ-MIX-022', name: 'High Shear Mixer', area: 'AREA-OSD', room: 'RM-OSD-101', status: 'Operational', calibrationDue: '2026-02-15', qualificationStatus: 'Qualified', lastPM: '2026-01-10', nextPM: '2026-02-10', openDeviations: 1, linkedReqs: ['SM-EQP-001'] },
  { id: 'EQ-BAL-017', name: 'Analytical Balance', area: 'AREA-QC', room: 'RM-QC-102', status: 'Blocked', calibrationDue: '2026-01-15', qualificationStatus: 'Qualified', lastPM: '2025-12-15', nextPM: '2026-01-15', openDeviations: 1, linkedReqs: ['SM-EQP-001'] },
  { id: 'EQ-GRAN-001', name: 'Fluid Bed Granulator', area: 'AREA-OSD', room: 'RM-OSD-103', status: 'Under Maintenance', calibrationDue: '2026-03-01', qualificationStatus: 'Qualified', lastPM: '2026-01-12', nextPM: '2026-02-12', openDeviations: 1, linkedReqs: ['SM-EQP-001'] },
  { id: 'EQ-COAT-001', name: 'Tablet Coating Machine', area: 'AREA-OSD', room: 'RM-OSD-105', status: 'Operational', calibrationDue: '2026-02-28', qualificationStatus: 'Qualified', lastPM: '2026-01-05', nextPM: '2026-02-05', openDeviations: 0, linkedReqs: ['SM-EQP-001'] },
  { id: 'EQ-DISS-003', name: 'Dissolution Apparatus', area: 'AREA-QC', room: 'RM-QC-101', status: 'Operational', calibrationDue: '2026-01-25', qualificationStatus: 'Qualified', lastPM: '2025-12-20', nextPM: '2026-01-20', openDeviations: 1, linkedReqs: ['SM-EQP-001'] },
  { id: 'AHU-STER-001', name: 'Air Handling Unit - Sterile', area: 'AREA-STER', room: 'AHU-ROOM', status: 'Under Investigation', calibrationDue: '2026-04-01', qualificationStatus: 'Requalification Required', lastPM: '2025-12-01', nextPM: '2026-01-25', openDeviations: 1, linkedReqs: ['SM-EQP-001', 'SM-HVAC-001'] },
  { id: 'CR-WH-001', name: 'Cold Room Unit', area: 'AREA-WH', room: 'RM-WH-COLD-01', status: 'Operational', calibrationDue: '2026-02-01', qualificationStatus: 'Qualified', lastPM: '2026-01-15', nextPM: '2026-02-15', openDeviations: 1, linkedReqs: ['SM-EQP-001'] },
  { id: 'LAF-STER-002', name: 'Laminar Air Flow Unit', area: 'AREA-STER', room: 'RM-STER-002', status: 'Operational', calibrationDue: '2026-01-30', qualificationStatus: 'Qualified', lastPM: '2025-12-15', nextPM: '2026-01-15', openDeviations: 0, linkedReqs: ['SM-EQP-001', 'SM-HVAC-001'] }
];

// Materials
export const materials = [
  { id: 'MAT-PARA-042', name: 'Paracetamol IP', supplier: 'SUP-001', status: 'Quarantine', coaStatus: 'Pending Review', storageCondition: '25°C/60% RH', expiryDate: '2027-06-15', receivedQty: '500 kg', lotNo: 'PARA-2026-042', linkedReqs: ['SM-MAT-001'] },
  { id: 'MAT-STARCH-018', name: 'Maize Starch IP', supplier: 'SUP-002', status: 'Released', coaStatus: 'Approved', storageCondition: '25°C/60% RH', expiryDate: '2027-03-20', receivedQty: '200 kg', lotNo: 'STR-2026-018', linkedReqs: ['SM-MAT-001'] },
  { id: 'MAT-MCC-025', name: 'Microcrystalline Cellulose', supplier: 'SUP-003', status: 'Released', coaStatus: 'Approved', storageCondition: '25°C/60% RH', expiryDate: '2027-08-10', receivedQty: '300 kg', lotNo: 'MCC-2026-025', linkedReqs: ['SM-MAT-001'] },
  { id: 'MAT-MGST-012', name: 'Magnesium Stearate', supplier: 'SUP-002', status: 'Released', coaStatus: 'Approved', storageCondition: '25°C/60% RH', expiryDate: '2027-12-01', receivedQty: '50 kg', lotNo: 'MGST-2026-012', linkedReqs: ['SM-MAT-001'] },
  { id: 'MAT-HPMC-008', name: 'HPMC E5', supplier: 'SUP-004', status: 'Quarantine', coaStatus: 'Under Testing', storageCondition: '25°C/60% RH', expiryDate: '2027-09-15', receivedQty: '100 kg', lotNo: 'HPMC-2026-008', linkedReqs: ['SM-MAT-001'] }
];

// Suppliers
export const suppliers = [
  { id: 'SUP-001', name: 'Granules India Ltd', status: 'Approved', lastAudit: '2025-08-15', nextAudit: '2026-08-15', rating: 'A', materials: ['Paracetamol IP', 'Ibuprofen IP'], linkedReqs: ['SM-SUP-001'] },
  { id: 'SUP-002', name: 'Sigachi Industries', status: 'Approved', lastAudit: '2025-10-01', nextAudit: '2026-10-01', rating: 'A', materials: ['Maize Starch IP', 'Magnesium Stearate'], linkedReqs: ['SM-SUP-001'] },
  { id: 'SUP-003', name: 'Avantor (JT Baker)', status: 'Approved', lastAudit: '2025-06-20', nextAudit: '2026-06-20', rating: 'A', materials: ['Microcrystalline Cellulose'], linkedReqs: ['SM-SUP-001'] },
  { id: 'SUP-004', name: 'Colorcon Asia', status: 'Conditional', lastAudit: '2025-11-10', nextAudit: '2026-05-10', rating: 'B', materials: ['HPMC E5', 'Opadry'], linkedReqs: ['SM-SUP-001'] }
];

// Batches
export const batches = [
  { id: 'BATCH-PCM-0126-0042', product: 'Paracetamol 500mg Tablets', status: 'In Progress', stage: 'Coating', startDate: '2026-01-16', targetCompletion: '2026-01-20', yield: 98.5, deviations: ['DEV-OSD-2026-014'] },
  { id: 'BATCH-PCM-0126-0041', product: 'Paracetamol 500mg Tablets', status: 'QC Testing', stage: 'Release Testing', startDate: '2026-01-14', targetCompletion: '2026-01-18', yield: 99.1, deviations: ['DEV-OSD-2026-013'] },
  { id: 'BATCH-PCM-0126-0040', product: 'Paracetamol 500mg Tablets', status: 'QC Testing', stage: 'Dissolution', startDate: '2026-01-12', targetCompletion: '2026-01-17', yield: 98.8, deviations: ['DEV-QC-2026-012'] },
  { id: 'BATCH-INJ-0126-0012', product: 'Paracetamol Injection 150mg/ml', status: 'Released', stage: 'Complete', startDate: '2026-01-05', targetCompletion: '2026-01-10', yield: 99.5, deviations: ['DEV-STER-2026-007'] }
];

// Audits
export const audits = [
  { id: 'AUD-SI-2026-001', type: 'Self-Inspection', scope: 'Block A – Oral Solid', status: 'Scheduled', scheduledDate: '2026-01-30', auditor: 'Dr. Anita Kulkarni', findings: [], linkedReqs: ['SM-SI-001', 'SM-AUD-001'] },
  { id: 'AUD-SI-2025-012', type: 'Self-Inspection', scope: 'Full Site', status: 'Completed', scheduledDate: '2025-12-15', auditor: 'Internal Audit Team', findings: [{ id: 'FIND-001', description: 'Calibration sticker missing on EQ-BAL-017', severity: 'Major', capaId: null }], linkedReqs: ['SM-SI-001', 'SM-AUD-001'] },
  { id: 'AUD-SUP-2026-001', type: 'Supplier Audit', scope: 'Granules India Ltd', status: 'Planned', scheduledDate: '2026-02-20', auditor: 'QA Team', findings: [], linkedReqs: ['SM-SUP-001', 'SM-AUD-001'] }
];

// Audit Packs
export const auditPacks = [
  { id: 'AP-2026-001', name: 'Q4 2025 Compliance Pack – Plant A', scope: { plant: 'PLANT-A', period: 'Oct-Dec 2025', products: ['Paracetamol 500mg'], areas: ['Block A', 'Warehouse'] }, status: 'Generated', generatedAt: '2026-01-10T14:00:00', generatedBy: 'Dr. Anita Kulkarni', evidenceCount: 245, integrityHash: 'SHA256:a3f2b8c9d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9', linkedReqs: ['SM-AUD-001', 'SM-DI-001'] },
  { id: 'AP-2025-004', name: 'WHO Pre-Qualification Audit Pack', scope: { plant: 'PLANT-A', period: 'Jul-Sep 2025', products: ['All Products'], areas: ['Full Site'] }, status: 'Archived', generatedAt: '2025-10-01T10:00:00', generatedBy: 'Dr. Anita Kulkarni', evidenceCount: 512, integrityHash: 'SHA256:b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3', linkedReqs: ['SM-AUD-001', 'SM-DI-001'] }
];

// PID Trace Metadata for each route
export const traceMetadata = {
  '/': {
    prd: ['PRD §3 – Goals & Metrics', 'PRD §7.1 – Role Dashboards', 'PRD §6.13 – Audit Pack Readiness'],
    req: ['SM-PQS-001', 'SM-DI-001', 'SM-AUD-001', 'SM-SI-001'],
    notes: 'Master dashboard showing compliance KPIs, at-risk items, and quick navigation to key workflows.'
  },
  '/tasks': {
    prd: ['PRD §6.3 – Mobile Execution App', 'PRD §6.2 – Checklist Management'],
    req: ['SM-DI-001', 'SM-DOC-001', 'SM-TRN-001'],
    notes: 'Task management with iPad Mode execution capability.'
  },
  '/tasks/execute': {
    prd: ['PRD §6.3 – Mobile Execution App', 'PRD §6.4 – Deviations/CAPA', 'PRD §10 – Audit Readiness'],
    req: ['SM-DEV-001', 'SM-DI-001', 'SM-SAN-001', 'SM-TRN-001'],
    notes: 'iPad Mode checklist execution with evidence capture and auto-deviation creation.'
  },
  '/checklists': {
    prd: ['PRD §6.2 – Checklist Builder', 'PRD §6.3 – Mobile Execution App'],
    req: ['SM-DOC-001', 'SM-DI-001'],
    notes: 'Checklist template management with version control.'
  },
  '/requirements': {
    prd: ['PRD §6.1 – Requirements Library', 'PRD §6.2 – Checklist Builder'],
    req: ['SM-PQS-001', 'SM-DOC-001', 'SM-DI-001', 'SM-CSV-001'],
    notes: 'Schedule-M and WHO-GMP requirement mapping with coverage heatmap.'
  },
  '/deviations': {
    prd: ['PRD §6.4 – Deviations/CAPA', 'PRD §10 – Audit Trail', 'PRD §11 – Reporting'],
    req: ['SM-DEV-001', 'SM-DI-001', 'SM-PQR-001'],
    notes: 'Deviation management with investigation workflow and root cause analysis.'
  },
  '/capa': {
    prd: ['PRD §6.4 – Deviations/CAPA', 'PRD §10 – Audit Trail'],
    req: ['SM-CAPA-001', 'SM-DI-001'],
    notes: 'CAPA tracking with effectiveness verification.'
  },
  '/change-control': {
    prd: ['PRD §6.5 – Change Control', 'PRD §10 – Audit Trail'],
    req: ['SM-CC-001', 'SM-QRM-001', 'SM-DI-001'],
    notes: 'Risk-based change control with multi-level approvals.'
  },
  '/audits': {
    prd: ['PRD §6.6 – Audit Management', 'PRD §6.4 – CAPA'],
    req: ['SM-SI-001', 'SM-CAPA-001', 'SM-EQP-001', 'SM-DI-001'],
    notes: 'Self-inspection and supplier audit management.'
  },
  '/audit-packs': {
    prd: ['PRD §6.13 – Audit Pack', 'PRD §10 – Audit Readiness', 'PRD §12 – Data Model'],
    req: ['SM-AUD-001', 'SM-DI-001', 'SM-DOC-001', 'SM-CSV-001'],
    notes: 'Audit pack generation with evidence indexing and integrity verification.'
  },
  '/assets': {
    prd: ['PRD §6.7 – Asset Management', 'PRD §6.8 – Utilities Monitoring'],
    req: ['SM-EQP-001', 'SM-HVAC-001', 'WHO-TRS-HVAC'],
    notes: 'Equipment and utilities management with calibration tracking.'
  },
  '/materials': {
    prd: ['PRD §6.9 – Materials Management', 'PRD §6.10 – Supplier Management'],
    req: ['SM-MAT-001', 'SM-SUP-001'],
    notes: 'Material receipt, quarantine, and release workflow.'
  },
  '/documents': {
    prd: ['PRD §6.11 – Document Control', 'PRD §6.12 – Training Management'],
    req: ['SM-DOC-001', 'SM-TRN-001'],
    notes: 'SOP management and training matrix.'
  },
  '/pqr': {
    prd: ['PRD §6.14 – PQR', 'PRD §11 – Reporting'],
    req: ['SM-PQR-001', 'SM-DEV-001', 'SM-CAPA-001'],
    notes: 'Product Quality Review dashboard with trend analysis.'
  },
  '/integrations': {
    prd: ['PRD §8 – Integrations', 'PRD §12 – Data Model'],
    req: ['SM-CSV-001', 'SM-DI-001'],
    notes: 'ERP/MES/LIMS/CMMS integration status and sync monitoring.'
  },
  '/admin': {
    prd: ['PRD §9 – Administration', 'PRD §7.2 – Role Management'],
    req: ['SM-DI-001', 'SM-TRN-001'],
    notes: 'User management, role configuration, and trace coverage map.'
  }
};

// Checklist Questions for Demo
export const checklistQuestions = [
  { id: 'Q1', text: 'Is the area free from previous product residues?', type: 'pass_fail', required: true, onFail: 'create_deviation' },
  { id: 'Q2', text: 'Are all equipment status labels current and visible?', type: 'pass_fail', required: true, onFail: 'create_deviation' },
  { id: 'Q3', text: 'Is the room temperature within specification (20-25°C)?', type: 'numeric', min: 20, max: 25, unit: '°C', required: true, onFail: 'create_deviation' },
  { id: 'Q4', text: 'Is the relative humidity within specification (40-60%)?', type: 'numeric', min: 40, max: 60, unit: '%', required: true, onFail: 'create_deviation' },
  { id: 'Q5', text: 'Line clearance complete and verified?', type: 'pass_fail', required: true, onFail: 'create_deviation' },
  { id: 'Q6', text: 'All personnel properly gowned?', type: 'pass_fail', required: true, onFail: 'create_deviation' },
  { id: 'Q7', text: 'Batch record available and verified?', type: 'pass_fail', required: true, onFail: 'create_deviation' },
  { id: 'Q8', text: 'Raw materials verified against BOM?', type: 'pass_fail', required: true, onFail: 'create_deviation' }
];

// Audit Trail Sample
export const auditTrailSample = [
  { timestamp: '2026-01-18T09:45:00', user: 'Priya Sharma', action: 'Created', field: 'Deviation', before: null, after: 'DEV-OSD-2026-014', reason: 'Auto-created from checklist failure' },
  { timestamp: '2026-01-18T09:46:00', user: 'Priya Sharma', action: 'Updated', field: 'Severity', before: 'Minor', after: 'Major', reason: 'Based on batch impact assessment' },
  { timestamp: '2026-01-18T10:00:00', user: 'Sneha Desai', action: 'Updated', field: 'Status', before: 'Open', after: 'Under Investigation', reason: 'Investigation initiated' }
];
